'use client'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight, ChevronDown } from 'lucide-react'
import { cn, slugify } from '@/lib/utils'
import type { Industry } from '@/types/exam'
import type { ExamSearchEntry } from '@/data/exams/search-index'
import { groupByIndustry } from './exam-search'
import { IndustrySpot } from './IndustrySpot'
import { LOGIN_URL, NAV_LINKS, RESOURCES_MENU, WHY_MENU } from './nav-links'

interface MobileMenuSectionsProps {
  entries: ExamSearchEntry[]
}

const ROW_CLASS =
  'text-ink hover:bg-surface flex min-h-11 items-center rounded-xl px-2 text-base font-medium transition-colors'

export function MobileMenuSections({ entries }: MobileMenuSectionsProps) {
  const groups = groupByIndustry(entries)
  const [expanded, setExpanded] = useState<Industry | null>(null)

  return (
    <nav aria-label="Site">
      <ul>
        {groups.map((group) => {
          const isExpanded = expanded === group.industry
          const groupId = `mobile-group-${slugify(group.industry)}`
          return (
            <li key={group.industry}>
              <button
                type="button"
                onClick={() => setExpanded(isExpanded ? null : group.industry)}
                aria-expanded={isExpanded}
                aria-controls={groupId}
                className="hover:bg-surface flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition-colors"
              >
                <IndustrySpot industry={group.industry} />
                <span className="text-ink flex-1 text-base font-medium">
                  {group.industry}
                </span>
                <span className="text-hint text-[13px] tabular-nums">
                  {group.entries.length}
                </span>
                <ChevronDown
                  size={16}
                  className={cn(
                    'text-hint transition-transform duration-200',
                    isExpanded && 'rotate-180'
                  )}
                />
              </button>
              {isExpanded && (
                <ul
                  id={groupId}
                  className="border-border-light mt-0.5 mb-1 ml-5 border-l pl-3"
                >
                  {group.entries.map((entry) => (
                    <li key={entry.slug}>
                      <Link
                        href={`/exam/${entry.slug}`}
                        className="hover:bg-surface flex min-h-11 flex-col justify-center rounded-lg px-2 py-1 transition-colors"
                      >
                        <span className="font-heading text-ink text-[15px] font-semibold slashed-zero">
                          {entry.name}
                        </span>
                        <span className="text-hint truncate text-[13px]">
                          {entry.full}
                        </span>
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link
                      href={`/exams?industry=${slugify(group.industry)}`}
                      className="text-primary flex min-h-11 items-center gap-1 rounded-lg px-2 text-sm font-medium"
                    >
                      All {group.industry}
                      <ArrowRight size={13} />
                    </Link>
                  </li>
                </ul>
              )}
            </li>
          )
        })}
      </ul>
      <div className="border-border my-2 border-t" />
      <ul>
        {NAV_LINKS.map((link) => (
          <li key={link.href}>
            <Link href={link.href} className={ROW_CLASS}>
              {link.label}
            </Link>
          </li>
        ))}
        <li className="text-hint px-2 pt-2 text-[11px] font-bold tracking-wide uppercase">
          {RESOURCES_MENU.label}
        </li>
        {RESOURCES_MENU.items.map((item) => (
          <li key={item.href}>
            <Link href={item.href} className={ROW_CLASS}>
              {item.label}
            </Link>
          </li>
        ))}
        <li className="text-hint px-2 pt-2 text-[11px] font-bold tracking-wide uppercase">
          {WHY_MENU.label}
        </li>
        {WHY_MENU.items.map((item) => (
          <li key={item.href}>
            <Link href={item.href} className={ROW_CLASS}>
              {item.label}
            </Link>
          </li>
        ))}
        <li>
          <Link
            href={LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={ROW_CLASS}
          >
            Log in
          </Link>
        </li>
      </ul>
    </nav>
  )
}
